import { useEffect, useState } from "react";
import { Briefcase, Compass, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { Seo } from "@/components/seo/Seo";
import { cn } from "@/lib/utils";

type RoleChoice = "founder" | "expert";

/**
 * Post-signup role chooser. Idempotent: if the user already chose a persona
 * (founder path) or already submitted a provider application (expert path),
 * we redirect them to the dashboard instead of asking again.
 */
export default function OnboardingRole() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState<RoleChoice | null>(null);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        navigate("/login", { replace: true });
        return;
      }
      const [{ data: profile }, { data: app }] = await Promise.all([
        supabase.from("profiles").select("persona").eq("id", u.user.id).maybeSingle(),
        supabase
          .from("provider_applications")
          .select("id")
          .eq("user_id", u.user.id)
          .limit(1)
          .maybeSingle(),
      ]);
      if (profile?.persona || app?.id) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setChecking(false);
    })();
  }, [navigate]);

  function pick(choice: RoleChoice) {
    setSubmitting(choice);
    if (choice === "founder") {
      navigate("/onboarding/persona");
    } else {
      navigate("/apply/form");
    }
  }

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Seo title={t("onboarding.role.seoTitle")} description={t("onboarding.role.seoDesc")} />
      <OnboardingShell step={1}>
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {t("onboarding.role.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {t("onboarding.role.subtitle")}
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2">
          {([
            { id: "founder", icon: Compass },
            { id: "expert", icon: Briefcase },
          ] as const).map(({ id, icon: Icon }) => {
            const isLoading = submitting === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => pick(id)}
                disabled={!!submitting}
                aria-label={t(`onboarding.role.${id}.label`)}
                className={cn(
                  "group relative flex flex-col items-start gap-3 rounded-2xl border-2 border-border bg-card p-6 text-left transition-all",
                  "hover:-translate-y-0.5 hover:border-primary/40 hover:bg-muted/30 hover:shadow-elevated",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  "disabled:opacity-60",
                )}
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="font-display text-lg font-bold">
                    {t(`onboarding.role.${id}.label`)}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t(`onboarding.role.${id}.desc`)}
                  </p>
                </div>
                {isLoading && (
                  <Loader2 className="absolute right-4 top-4 h-4 w-4 animate-spin text-primary" />
                )}
              </button>
            );
          })}
        </div>

        <p className="mx-auto mt-8 max-w-md text-center text-xs text-muted-foreground">
          {t("onboarding.role.changeLater")}
        </p>
      </OnboardingShell>
    </>
  );
}